/**
 * Minimal promise wrapper over one IndexedDB object store (key → structured-clone value).
 * Structured clone keeps a non-extractable CryptoKey non-extractable, which is why device keys
 * live here and not in localStorage. Works in the page and in the service worker.
 */
const DB_NAME = 'viko'
const STORE = 'kv'

let dbp: Promise<IDBDatabase> | null = null

function open(): Promise<IDBDatabase> {
  if (dbp) return dbp
  dbp = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => {
      dbp = null
      reject(req.error)
    }
  })
  return dbp
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode)
        const req = fn(t.objectStore(STORE))
        t.oncomplete = () => resolve(req.result)
        t.onerror = () => reject(t.error)
        t.onabort = () => reject(t.error)
      }),
  )
}

/** Atomically read/update one account record across tabs and service workers. */
async function update<T>(key: string, change: (current: T | undefined) => T): Promise<T> {
  const db = await open()
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, 'readwrite')
    const store = t.objectStore(STORE)
    const req = store.get(key)
    let result: T
    let failure: unknown
    req.onsuccess = () => {
      try {
        result = change(req.result as T | undefined)
        store.put(result, key)
      } catch (error) {
        failure = error
        t.abort()
      }
    }
    t.oncomplete = () => resolve(result)
    t.onerror = () => reject(failure ?? t.error)
    t.onabort = () => reject(failure ?? t.error)
  })
}

/**
 * "Just this session" (lib/session-only.ts): while set, every read and write goes to this
 * page-lifetime map and IndexedDB is never opened for writing — the device key, trust pins and
 * content keys vanish with the tab. Values are structured-cloned like IndexedDB would (a
 * non-extractable CryptoKey stays non-extractable).
 */
let memory: Map<string, unknown> | null = null

export const idb = {
  update: <T>(key: string, change: (current: T | undefined) => T): Promise<T> => {
    if (!memory) return update(key, change)
    const store = memory
    return Promise.resolve().then(() => {
      const next = change(structuredClone(store.get(key)) as T | undefined)
      store.set(key, structuredClone(next))
      return next
    })
  },
  get: <T>(key: string): Promise<T | undefined> => {
    if (memory) return Promise.resolve(structuredClone(memory.get(key)) as T | undefined)
    return tx<T | undefined>('readonly', (s) => s.get(key) as IDBRequest<T | undefined>)
  },
  set: (key: string, value: unknown): Promise<void> => {
    if (memory) {
      memory.set(key, structuredClone(value))
      return Promise.resolve()
    }
    return tx('readwrite', (s) => s.put(value, key)).then(() => {})
  },
  del: (key: string): Promise<void> => {
    if (memory) {
      memory.delete(key)
      return Promise.resolve()
    }
    return tx('readwrite', (s) => s.delete(key)).then(() => {})
  },
  /** Switch this page to memory-only storage (idempotent). */
  enterMemoryOnly() {
    memory ??= new Map()
  },
  /** Leave memory-only storage, wiping everything it held. */
  dropMemory() {
    memory?.clear()
    memory = null
  },
  get memoryOnly() {
    return memory !== null
  },
  /** Test hook: forget the cached connection (fake-indexeddb resets between tests). */
  _reset() {
    dbp = null
    memory = null
  },
}
